import { NutrientUI } from "@corpuscomplete/core-ui/nutrition/nutrient";
import { Nutrient } from "@corpuscomplete/core/nutrition/nutrient";
import { cn } from "@corpuscomplete/web-ui/lib/utils";

import { Text } from "@corpuscomplete/web-ui/components/text";
import { TriangleAlertIcon } from "lucide-react";

/**
 * Inline nutrient amount label row for foods or computed totals.
 * Contract: formatting follows core-ui nutrient display order and labels,
 * while this component controls compact web typography only.
 */
export function NutrientAmountsLabel({
  className,
  fractionDigits = 0,
  abbreviated = false,
  textMuted = abbreviated,
  hasIncompleteData = false,
  ...props
}: NutrientUI.NutrientAmountsLabelProps) {
  const { nutrients, nutrientKeys } = NutrientUI.useDisplayNutrients();
  const nutrientAmounts = NutrientUI.nutrientValuesAsAmounts(props, nutrients);
  const nutrientEntries = Nutrient.amountsToInfo(nutrientAmounts, nutrients, nutrientKeys);

  return (
    <div className={cn("flex flex-row items-center gap-2", className)}>
      {hasIncompleteData && <TriangleAlertIcon className="h-3 w-3 text-yellow-500" />}
      {nutrientEntries.map(({ nutrient, amount }) => {
        const text = NutrientUI.nutrientLabel(nutrient, abbreviated);
        return (
          <Text variant="xs" key={nutrient.id} className={cn(textMuted && "text-muted-foreground")}>
            {text}
            {amount.toFixed(fractionDigits)}
            {nutrient.unit?.defaultName}
          </Text>
        );
      })}
    </div>
  );
}

/**
 * Inline nutrient target labels for configured daily goals.
 * Contract: renders variant-aware target text for nutrients selected by
 * core-ui display settings.
 */
export function NutrientTargetsLabel({
  className,
  fractionDigits = 0,
  abbreviated = false,
  textMuted = abbreviated,
  targets,
}: NutrientUI.NutrientTargetsLabelProps) {
  const { nutrients } = NutrientUI.useDisplayNutrients();

  return (
    <div className={cn("flex flex-row gap-2", className)}>
      {nutrients.map((nutrient) => {
        const target = nutrient.key && targets[nutrient.key];
        const text = NutrientUI.nutrientLabel(nutrient, abbreviated);
        if (!target) {
          return null;
        }
        const formattedTarget = NutrientUI.formatTarget(
          target,
          nutrient.unit?.defaultName,
          fractionDigits,
        );
        if (!formattedTarget) {
          return null;
        }
        return (
          <Text key={nutrient.id} className={cn(textMuted && "text-muted-foreground")}>
            {text}
            {formattedTarget}
          </Text>
        );
      })}
    </div>
  );
}
